import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "../Modal/Modal";
import { ModalBody, Text, Textarea } from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import { Share, Comment, VerticalMoreIcon } from "../../Assets/SvgIcons";
import {
  PostCardWrapper,
  CardHeader,
  CommentButton,
  Author,
  NameAndTime,
} from "./styles";
import { Props } from "./types";
import { FloatingDropdown } from "../FloatingDropdown/FloatingDropdown";
import { Comments } from "../Comments/Comments";
import { Reactions } from "../Reactions/Reactions";
import { ProfileAvatar } from "../ProfileAvatar/ProfileAvatar";
import { usePost } from "../../hooks/usePost";
import queryStrings from "query-string";
import { CaptionComponent } from "../CaptionComponent/CaptionComponent";
import { truncate } from "../../utils/helper";
import { LinkPreview } from "../LinkPreview/LinkPreview";
import { useUpdatePostMutation } from "../../../redux/reducers/apiSlice";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/react";

const MAX_CHAR_COUNT = 2500;

export const PostCard = ({
  post,
  sharedPost,
  onShare,
  commentsFullHeight,
}: Props) => {
  const [isFloatingDropdownVisible, setIsFloatingDropdownVisible] =
    useState(false);
  const [showComment, setShowComment] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedCaption, setEditedCaption] = useState(post?.caption || "");
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [updatePost, { isLoading: updatePostLoading }] = useUpdatePostMutation();

  const navigate = useNavigate();

  const {
    deletePostLoading,
    handleDeletePost,
    comments,
    commentCount,
    optionsEnabled,
  } = usePost(post);

  useEffect(() => {
    const queryString = queryStrings.parse(location.search);
    if (queryString?.showComment && queryString.showComment === "true") {
      setShowComment(true);
    }
  }, [location.search]);
  const isTruncated =
    (post?.ownerType === "USER" && post?.ownerName?.length > 68) ||
    (post?.ownerType === "ORGANIZATION" && post?.ownerName?.length > 127);

  const firstUrlInCaption = useMemo(() => {
    if (!post?.caption) return null;
    const URL_REGEX = /(https?:\/\/[^\s]+)/;
    const match = post.caption.match(URL_REGEX);
    return match ? match[0] : null;
  }, [post?.caption]);

  const isEdited = useMemo(() => {
    if (!post?.updatedAt || !post?.createdAt) return false;
    return new Date(post.updatedAt).getTime() > new Date(post.createdAt).getTime();
  }, [post?.updatedAt, post?.createdAt]);

  useEffect(() => {
    if (showEditModal) {
      setEditedCaption(post?.caption || "");
    }
  }, [showEditModal, post?.caption]);

  const handleSavePostEdit = async () => {
    const trimmedCaption = editedCaption.trim();
    const hasCaptionChanged = trimmedCaption !== (post?.caption || "").trim();

    if (!hasCaptionChanged) {
      setShowEditModal(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("caption", trimmedCaption);
      await updatePost({ id: post.id, postData: formData }).unwrap();
      toast.success("Post updated successfully");
      setShowEditModal(false);
      setIsFloatingDropdownVisible(false);
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Unable to update this post. Please try again.");
    }
  };

  const normalizedOriginalCaption = (post?.caption || "").trim();
  const normalizedEditedCaption = editedCaption.trim();
  const hasUnsavedChanges = normalizedEditedCaption !== normalizedOriginalCaption;
  const remainingCharacters = MAX_CHAR_COUNT - editedCaption.length;
  const isSaveDisabled =
    updatePostLoading || editedCaption.length > MAX_CHAR_COUNT || !hasUnsavedChanges;

  const handleCloseEditModal = () => {
    if (hasUnsavedChanges) {
      const shouldDiscard = window.confirm("Discard your post changes?");
      if (!shouldDiscard) return;
    }
    setShowEditModal(false);
  };

  return (
    <PostCardWrapper isShared={!!sharedPost} loading={deletePostLoading}>
      <CardHeader>
        <Author
          onClick={() =>
            navigate(
              post.ownerType === "USER"
                ? `/users/${post?.ownerId}`
                : `/organizations/${post.ownerId}/profile`
            )
          }
        >
          <ProfileAvatar
            border={`2px solid #eee`}
            src={post?.ownerProfilePhoto}
            name={post?.ownerName}
          />
          <NameAndTime
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            $isHoveredAndTruncated={isHovered && isTruncated}
          >
            <span className="name">
              {post?.ownerType === "USER" &&
                post?.ownerName &&
                !isHovered &&
                truncate(post?.ownerName)}
              {post?.ownerType === "ORGANIZATION" &&
                post?.ownerName &&
                !isHovered &&
                truncate(post?.ownerName, 127)}
              {isHovered && post?.ownerName}
            </span>
            <span className="time">
              {post?.updatedAt &&
                formatDistanceToNow(new Date(post.updatedAt), {
                  addSuffix: true,
                }).replace("about", "")}
              {isEdited ? " • Edited" : ""}
            </span>
          </NameAndTime>
        </Author>
        <div className="more-icon-container">
          {isFloatingDropdownVisible && optionsEnabled && (
            <FloatingDropdown
              onOutsideClicked={() => setIsFloatingDropdownVisible(false)}
              items={[
                {
                  label: "Edit post",
                  action: () => {
                    setShowEditModal(true);
                    setIsFloatingDropdownVisible(false);
                  },
                },
                {
                  label: "Delete post",
                  action: () => setShowDeleteModal(true),
                  isDestructiveAction: true,
                },
              ]}
              visible={isFloatingDropdownVisible}
            />
          )}
          {!sharedPost && (
            <button
              disabled={!optionsEnabled}
              onClick={() =>
                setIsFloatingDropdownVisible((currentlyVisible) => !currentlyVisible)
              }
              id="cypress-menu-tap"
            >
              <VerticalMoreIcon data-cy="test" color={optionsEnabled ? "black" : "#eee"} />{" "}
            </button>
          )}
        </div>
      </CardHeader>
      <p className="description">
        <CaptionComponent caption={post?.caption} />
      </p>

      {firstUrlInCaption && !(post.type === "MEDIA" && post?.media && post.media.length > 0) && (
        <div style={{ marginTop: 8 }}>
          <LinkPreview url={firstUrlInCaption} />
        </div>
      )}

      {post.type === "MEDIA" && post?.media && post.media.length > 0 && (
        <>
          {post.media.length === 1 ? (
            <div className="single-image-container">
              <img src={post.media[0].url} />
            </div>
          ) : (
            <div className="image-container">
              {post.media.slice(0, 4).map((mediaItem, index) => {
                if (!mediaItem?.url) return null;
                const isLast = index === 3;
                const remaining = post.media.length - 4;

                return (
                  <div key={mediaItem.url + index} className="image-tile">
                    <img src={mediaItem.url} />
                    {isLast && remaining > 0 && (
                      <div className="image-more-overlay">+{remaining}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      {!sharedPost && onShare && (
        <>
          <div className="divider" />
          <div className="actions-container">
            <button onClick={() => onShare(post)} className="icon" id="cypress-share-post">
              <Share />
            </button>
            <CommentButton
              onClick={() => setShowComment((showComment) => !showComment)}
              className="icon"
              id="cypress-comment-a-post"
            >
              <Comment /> <span className="comment-count">{commentCount}</span>
            </CommentButton>
            <Reactions post={post} />
          </div>
          <Comments
            postId={post?.id}
            comments={comments}
            visible={showComment}
            fullHeight={commentsFullHeight}
          />
        </>
      )}
      <Modal
          cypressModalHeaderSelector="cypress-are-you-sure-selector"
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onPrimaryButtonClicked={() => {
            setIsFloatingDropdownVisible(false);
            handleDeletePost();
          }}
          modalTitle="Are you sure you want to delete this post?"
          primaryLoading={deletePostLoading}
          disablePrimaryButton={deletePostLoading}
          primaryButtonText="Delete"
          secondaryButtonText="Cancel"
        />
      <Modal
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          onPrimaryButtonClicked={handleSavePostEdit}
          modalTitle="Edit post"
          modalSubtitle="Make changes to your post. Edits are visible right away."
          primaryLoading={updatePostLoading}
          disablePrimaryButton={isSaveDisabled}
          primaryButtonText="Save"
          secondaryButtonText="Cancel"
        >
          <ModalBody paddingBottom="20px">
            <Textarea
              autoFocus
              value={editedCaption}
              onChange={(event) => {
                if (event.target.value.length <= MAX_CHAR_COUNT) {
                  setEditedCaption(event.target.value);
                }
              }}
              placeholder="What's happening?"
              minHeight="120px"
              resize="vertical"
            />
            <Text
              textAlign="right"
              marginTop="6px"
              fontSize="12px"
              color={remainingCharacters <= 100 ? "orange.500" : "gray.500"}
            >
              {remainingCharacters} characters remaining
            </Text>
          </ModalBody>
        </Modal>
    </PostCardWrapper>
  );
};
